import { useContext } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { rem } from "@mantine/core";
import { useForm, UseFormReturnType, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import { PhysicalExamTypes } from "@/fhir/enums/physical-exams";
import { GynecologicalPhysicalExamsQuestionnaires } from "@/fhir/enums/questionnaires";
import usePhysicalExamInitialValues from "@/physical-exams/hooks/use-physical-exam-initial-values";
import usePhysicalExamQuestionnaire from "@/physical-exams/hooks/use-physical-exam-questionnaire";
import usePhysicalExamUnsavedChanges from "@/physical-exams/hooks/usePhysicalExamUnsavedChanges";
import PhysicalExamHeader from "@/physical-exams/PhysicalExamHeader";
import PhysicalExamList from "@/physical-exams/PhysicalExamList";
import {
  amendPhysicalExamQuestionnaireResponse,
  createPhysicalExamQuestionnaireResponse,
  getMainQuestionnaireUrlFromEncounter,
} from "@/physical-exams/services/physical-exams";
import { GYNECOLOGICAL_ABDOMEN_FORM_INITIAL_VALUES } from "@/physical-exams/types/gynecological-abdomen";
import { PhysicalExamProps } from "@/physical-exams/types/physical-exams";
import {
  PhysicalExamForm,
  PhysicalExamFormSchema,
} from "@/physical-exams/zod-schemas/physical-exams";
import { useTemporarySubmit } from "@/utils/hooks";

const GynecologicalAbdomenPhysicalExam = ({
  url,
  submitTrigger,
  editable,
  isAmendment,
}: PhysicalExamProps) => {
  const { patient } = useContext(PatientContext);
  const { encounter } = useContext(EncounterContext);
  const { sections, questionnaire } = usePhysicalExamQuestionnaire(
    GynecologicalPhysicalExamsQuestionnaires.AbdomenPhysicalExam,
  );
  const medplum = useMedplum();
  const form = useForm<PhysicalExamForm>({
    initialValues: GYNECOLOGICAL_ABDOMEN_FORM_INITIAL_VALUES,
    validate: zodResolver(PhysicalExamFormSchema),
  });
  const formSetValues = form.setValues;
  const [initialValues, questionnaireResponse, isLoading] = usePhysicalExamInitialValues(
    questionnaire?.id,
    encounter,
    formSetValues,
    GYNECOLOGICAL_ABDOMEN_FORM_INITIAL_VALUES,
  );
  const { markAsSaved } = usePhysicalExamUnsavedChanges(url, form, initialValues);

  const practitioner = medplum.getProfile() as Practitioner;

  const handleSubmit = async (values: PhysicalExamForm) => {
    const mainQuestionnaireUrl = getMainQuestionnaireUrlFromEncounter(encounter!);

    // If questionnaireResponse is undefined, then the amend action shouldn't happen
    // since the PE answers are being created at this point.
    if (isAmendment && questionnaireResponse) {
      await amendPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    } else {
      await createPhysicalExamQuestionnaireResponse(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    }
    markAsSaved();
  };

  useTemporarySubmit<PhysicalExamForm>(form, submitTrigger, handleSubmit);
  if (!patient?.id || isLoading) return <Loading height={rem(96)} />;

  return (
    <form
      data-testid="gynecological-physical-exam-abdomen-form"
      onSubmit={form.onSubmit((values, _) => handleSubmit(values))}
    >
      <PhysicalExamHeader
        url={url}
        form={form}
        title="Abdomen"
        sections={sections}
        canDelete={editable}
        clientId={patient!.id!}
        showQuickSelection={editable}
        value={PhysicalExamTypes.GynecologicalAbdomen}
      >
        <PhysicalExamList
          sections={sections}
          editable={editable}
          questionnaire={questionnaire}
          form={form as UseFormReturnType<PhysicalExamForm>}
        />
      </PhysicalExamHeader>
    </form>
  );
};

export default GynecologicalAbdomenPhysicalExam;
